import { useMutation } from '@tanstack/react-query'
import { api, unwrap } from '@/api/client'
import { ApiError } from '@/api/errors'

/**
 * Three-step direct-to-storage upload: presign → PUT bytes to S3 → complete.
 * Resolves to the file_id, ready to attach to a report.
 */
export function useUploadFile() {
  return useMutation({
    mutationFn: async (file: File): Promise<string> => {
      const mime = file.type || 'application/octet-stream'
      const presign = await unwrap(
        api.POST('/api/v1/files/presign', {
          body: { original_name: file.name, mime_type: mime, size_bytes: file.size },
        }),
      )
      const put = await fetch(presign.upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': mime },
      }).catch((cause: unknown) => {
        throw ApiError.network(cause)
      })
      if (!put.ok) {
        throw new ApiError(put.status, 'INTERNAL', 'Не удалось загрузить файл в хранилище')
      }
      await unwrap(api.POST('/api/v1/files/complete', { body: { file_id: presign.file_id } }))
      return presign.file_id
    },
  })
}

/** Fetch a short-lived presigned download URL for a file id. */
export function fetchDownloadUrl(id: string): Promise<string> {
  return unwrap(api.GET('/api/v1/files/{id}', { params: { path: { id } } })).then(
    (r) => r.download_url,
  )
}
